import { useAppSelector } from 'modules/state-mgmt/store';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getTempTradeId } from 'utils/onboarding';
import { PAYMENT_TYPE, YEARS_OF_EXPERIENCE, YES_NO_OPTIONS } from 'utils/optionsUtil';
import { FIELD_CODE } from '../types';

const findProjectField = (projectConsentFields: any[], fieldId: string) => {
  return projectConsentFields.find(
    pf => pf.consentFormFieldId === fieldId && pf.isVisible && pf.isSetOnProjectConsentForm,
  );
};

const getActiveAndApprovedItems = (items: any[]) => {
  return items.filter(item => item.isActive && item.isApproved && (item.isMaster || !item.isMaster));
};

const getTranslation = (item: any, languageCode: string) => {
  if (item.translations?.length) {
    const translation = item.translations.find((tr: any) => tr.languageCode === languageCode);
    return translation?.name || item.name;
  }
  return item.name;
};

const isOtherOption = (optionName: string) => {
  return ['other', 'otro', 'otra'].includes(optionName?.toLowerCase?.());
};

const getDropDownOptions = (
  field: any,
  translate: (key: string, options?: Record<string, any>) => string,
  { languageCode, jobTitles, tradeStatus, socJobTitles, languageTurnerProtocol, skilledTrades, onboardingTrades },
) => {
  if (field?.consentFormFieldListItems) {
    let result = [];

    if (field.code === FIELD_CODE.PROJECT_SKILLED_TRADE) {
      let trades = [];
      const otherTrades = [];

      onboardingTrades.forEach(trade => {
        const matchedTrade = skilledTrades.find(skilledTrade => skilledTrade.id === trade.id) || null;
        if (!isOtherOption(trade.name)) {
          if (!matchedTrade) {
            // Unapproved trades are missing from /SkilledTrades but still carry their real id.
            otherTrades.push({ id: trade.id, name: trade.name, isSaved: false });
          } else {
            if (matchedTrade?.masterList) {
              trades?.push(matchedTrade);
            } else {
              otherTrades.push(matchedTrade);
            }
          }
        }
      });

      if (otherTrades.length > 0) {
        trades = [
          ...trades,
          ...otherTrades.map((o: any) => ({
            id: o.id || getTempTradeId(o.name),
            name: translate('common.otherValue', { value: o.name }),
          })),
        ];
      }

      const restTrades =
        skilledTrades?.filter(skilledTrade => {
          return (
            !trades.find(t => t.id === skilledTrade.id) && !isOtherOption(skilledTrade.name) && skilledTrade?.masterList
          );
        }) || [];

      trades = [...trades, ...restTrades];

      result = trades.map(i => ({
        id: i.id,
        name: getTranslation(i, languageCode),
      }));
    } else {
      result = getActiveAndApprovedItems(field.consentFormFieldListItems).map(item => ({
        id: item.id,
        name: getTranslation(item, languageCode),
      }));
    }

    return result;
  }

  // Handle cases when consentFormFieldListItems is absent
  switch (field.code) {
    case FIELD_CODE.JOB_TITLE:
      return jobTitles.filter((j: any) => j.masterList);
    case FIELD_CODE.TRADE_STATUS:
      return tradeStatus;
    case FIELD_CODE.SECTION_3_EMPLOYEE:
    case FIELD_CODE.SECTION_3_RESIDENT:
    case FIELD_CODE.STEP_STATUS:
    case FIELD_CODE.ELIGIBLE_TO_WORK_IN_US:
    case FIELD_CODE.LGBTQ:
      return YES_NO_OPTIONS[languageCode || 'en'];
    case FIELD_CODE.SOC_JOB_TITLE:
      return socJobTitles;
    case FIELD_CODE.YEARS_OF_EXPERIENCE:
      return YEARS_OF_EXPERIENCE;
    case FIELD_CODE.PAYMENT_TYPE:
      return PAYMENT_TYPE[languageCode || 'en'];
    case FIELD_CODE.LANGUAGE_TURNER_PROTOCOL:
      return languageTurnerProtocol;
    case FIELD_CODE.PROJECT_SKILLED_TRADE:
      const skilledTradesList = skilledTrades || [];
      const tradesList = (onboardingTrades || [])?.map((t: any) => t.id) || [];
      return skilledTradesList.filter((t: any) => tradesList.includes(t.id)) || [];
    default:
      return [];
  }
};

export const useConsentFormFields = () => {
  const { t } = useTranslation();
  const {
    employers,
    projectConsentFields,
    consentFormFields,
    languageCode,
    onboardingTrades,
    tradeStatus,
    jobTitles,
    socJobTitles,
    languageTurnerProtocol,
    skilledTrades,
  } = useAppSelector(state => {
    const languageCode = state.app?.languageCode;
    return {
      employers: state.onboarding?.companies || [],
      onboardingTrades: state.onboarding.trades || [],
      languageCode: state.app?.languageCode,
      projectConsentFields: state.cache.project.consentFormFields,
      jobTitles: state.cache.jobTitles[languageCode] || [],
      tradeStatus: state.cache.tradeStatus[languageCode] || [],
      socJobTitles: state.cache.socJobTitles[languageCode] || [],
      languageTurnerProtocol: state.cache.languageTurnerProtocol[languageCode] || [],
      skilledTrades: state.cache.skilledTrades[languageCode] || [],
      consentFormFields: state.cache.consentFormFields[languageCode] || [],
    };
  });

  const fields = useMemo(() => {
    // Early returns for invalid data
    if (!projectConsentFields || !consentFormFields || !Array.isArray(consentFormFields)) {
      return [];
    }

    const fields = [];
    consentFormFields.forEach((cacheField: any) => {
      const projectField = findProjectField(projectConsentFields, cacheField.id);

      if (projectField) {
        fields.push({
          ...cacheField,
          name: getTranslation(cacheField, languageCode),
          projectField,
          options: getDropDownOptions(cacheField, t, {
            languageCode,
            jobTitles,
            tradeStatus,
            socJobTitles,
            languageTurnerProtocol,
            skilledTrades,
            onboardingTrades,
          }),
        });
      }
    });

    return fields?.sort((a, b) => {
      if (a?.order == b?.order) {
        return a?.name.localeCompare(b?.name);
      } else {
        return a?.order - b?.order;
      }
    });
  }, [employers, projectConsentFields, consentFormFields, languageCode]);

  return { consentFormFields: fields };
};
